import { observer } from 'mobx-react';
import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Navigate, Route } from 'react-router-dom';
import accountStore from './store/accountStore';
import { HomeLinkRoutes } from '~/Routes';
import Loader from '~/component/loader';
import ErrorBoundary from '~/component/ErrorBoundary';

// Lazy load components with error handling for chunk loading failures
const lazyWithRetry = (componentImport: () => Promise<any>) => {
  return lazy(async () => {
    try {
      return await componentImport();
    } catch (error: any) {
      // If chunk loading fails (e.g., after deployment), reload the page
      if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk')) {
        console.warn('Chunk load error detected, reloading page...', error);
        // Reload after a short delay to allow the error to be logged
        setTimeout(() => {
          window.location.reload();
        }, 100);
        // Return a placeholder component that shows loading
        return {
          default: () => (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
              <Loader />
            </div>
          ),
        };
      }
      throw error;
    }
  });
};

const Login = lazyWithRetry(() => import('~/ui/login'));
const Home = lazyWithRetry(() => import('~/ui/home'));
const TempPermission = lazyWithRetry(() => import('~/ui/tempPermission'));
const DeviceTempPermission = lazyWithRetry(() => import('~/ui/deviceTempPermission'));
// Standalone role pages — one folder per permission under src/ui/
const Calendar = lazyWithRetry(() => import('~/ui/calendar'));
const Hydro = lazyWithRetry(() => import('~/ui/hydro'));
const AccountManager = lazyWithRetry(() => import('~/ui/accountManager'));
const Mamoleo = lazyWithRetry(() => import('~/ui/mamoleo'));

// Loading fallback component
const LoadingFallback = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      width: '100%',
    }}
  >
    <Loader />
  </div>
);

const App = observer(() => {
  // Check if user is authenticated: token exists AND userId is set (loginSuccess completed)
  // This ensures the user has actually logged in, not just has a token in storage
  const isAuth = !!accountStore.getToken && !!accountStore.userId;
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public / token-based — no account login */}
            <Route
              path={HomeLinkRoutes.tempPermission}
              element={<TempPermission />}
            />
            <Route
              path={HomeLinkRoutes.deviceTempPermission}
              element={<DeviceTempPermission />}
            />
            <Route
              path={HomeLinkRoutes.login}
              element={isAuth ? <Home /> : <Login />}
            />
            {/* Standalone permission UIs — src/ui/<featureName>/ */}
            <Route
              path={HomeLinkRoutes.calendar}
              element={isAuth ? <Calendar /> : <Login />}
            />
            <Route
              path={HomeLinkRoutes.hydro}
              element={isAuth ? <Hydro /> : <Login />}
            />
            <Route
              path={HomeLinkRoutes.accountManager}
              element={isAuth ? <AccountManager /> : <Login />}
            />
            <Route
              path={HomeLinkRoutes.mamoleo}
              element={isAuth ? <Mamoleo /> : <Login />}
            />
            <Route
              path={'*'}
              element={
                isAuth ? <Home /> : <Navigate replace to={HomeLinkRoutes.login} />
              }
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
});

export default App;
